import React, { CSSProperties, useState, useEffect } from 'react';
import {
  CircularProgress,
  Grid,
  Table,
  TableRow,
  Tabs,
  Tooltip,
  makeStyles,
  IconButton
} from '@material-ui/core';
import TableComponents from 'components/Table/';
import Tables from 'components/Table';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { Button, Prompt } from 'components/Base';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import Fields from 'components/Fields';
import api from 'api';
import { Notify, formatPercent, unFormatPercent } from 'utils';
import EmptyState from 'components/Base/EmptyState/EmptyState';
import Tab from 'components/Base/Tabs/FixedTab';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CloseIcon from '@material-ui/icons/Close';
import ModelPortfolioSelector from 'components/CipManagementv2/components/ModelPortfolioSelector';
import { buildFormData } from 'components/Forms/Utils/convertToFormData';
import { connect } from 'react-redux';
import actions from 'store/actions';

const useStyles = makeStyles({
  iconHolder: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '36px',
    height: '48px'
  },
  '@global': {
    '.MuiSvgIcon-colorSecondary': {
      color: '#0080bd !important'
    }
  }
});

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  image: Yup.mixed().nullable(),
  booking_link: Yup.string().url('Invalid URL').nullable(),
  date: Yup.string().nullable(),
  start_time: Yup.string().nullable(),
  end_time: Yup.string()
    .nullable()
    .test(
      'is-later',
      'End time must be later than start time',
      function (value) {
        const { start_time } = this.parent;
        if (start_time && value) {
          return value > start_time;
        }
        return true;
      }
    ),
  location: Yup.string().nullable()
});

interface PortfoliosProps {
  data: any;
  updateData: Function;
  classes: any;
  getMpsFirms: Function;
}

interface RangeType {
  id?: number;
  name: string;
  categories: any;
}

interface PortfolioType {
  id?: number;
  name?: number;
  model_portfolio_selection?: number;
  management_charge: string;
  initial_charge: string;
  factsheet?: string | null;
  index?: number;
  model_portfolio_selection_id?: number;
  file?: string | null;
}

const Portfolios = ({
  data,
  updateData,
  classes,
  getMpsFirms
}: PortfoliosProps) => {
  classes = { ...classes, ...useStyles() };
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPortfolios, setCurrentPortfolios] = useState([] as any);
  const [currentPortfolio, setCurrentPortfolio] = useState(
    null as PortfolioType | null
  );
  const [isModelPortfolioSelectorOpen, setIsModelPortfolioSelectorOpen] =
    useState(false);
  const [currentRange, setCurrentRange] = useState(
    data.portfolio_ranges.length ? data.portfolio_ranges[0] : null
  );
  const [isDeleteRangePromptOpen, setIsDeleteRangePromptOpen] = useState(
    false as any
  );
  const [isDeletePortfolioPromptOpen, setIsDeletePortfolioPromptOpen] =
    useState(false as any);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
  };

  const tabProps = (index: any) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
      style: {
        borderBottom: '2px solid #eaeaea',
        textTransform: 'none'
      } as CSSProperties
    };
  };

  const findRangeIndex = (id: number) => {
    return data.portfolio_ranges.findIndex(
      (range: RangeType) => range.id === id
    );
  };

  const addRange = () => {
    updateData({
      ...data,
      portfolio_ranges: [
        ...data.portfolio_ranges,
        { name: 'New range', categories: [], portfolios: [] }
      ]
    });
  };

  const createOrUpdateRange = (rangeData: any) => {
    const portfolioRanges = [...data.portfolio_ranges];
    portfolioRanges[tab] = { ...rangeData };

    updateData({ ...data, portfolio_ranges: portfolioRanges });
  };

  const onDelete = (success = true) => {
    setIsDeleting(false);
    setIsDeleteRangePromptOpen(false);
    if (success) {
      setTab(0);
    }
  };

  const deleteRange = (id: number) => {
    setIsDeleting(true);

    if (!id) {
      const newRanges = data.portfolio_ranges;
      newRanges.splice(tab, 1);
      updateData({ ...data, portfolio_ranges: newRanges });
      Notify.success('Portfolio Range deleted successfully.');
      onDelete();
      return;
    }

    api
      .deletePortfolioRange(data.id, id)
      .then(() => {
        const index = findRangeIndex(id);
        const newRanges = data.portfolio_ranges;
        newRanges.splice(index, 1);
        updateData({ ...data, portfolio_ranges: newRanges });
        Notify.success('Portfolio Range deleted successfully.');
        onDelete();
      })
      .catch(() => {
        Notify.error('Portfolio Range could not be deleted.');
        onDelete(false);
      });
  };

  const deletePortfolio = (index: number, currentValues: any) => {
    setIsDeleting(true);
    const portfolios = [...currentValues.portfolios];
    portfolios.splice(index, 1);
    setCurrentPortfolios(portfolios);
    setIsDeleting(false);
    setIsDeletePortfolioPromptOpen(false);
  };

  const addPortfolio = (selections: any, currentValues: any) => {
    const newPortfolios = [...currentValues.portfolios];

    selections.forEach((selection: any) => {
      const match = newPortfolios.find(
        (portfolio: PortfolioType) =>
          portfolio.model_portfolio_selection === Number(selection.value)
      );

      if (match) {
        return;
      }

      newPortfolios.push({
        name: selection.name,
        model_portfolio_selection: Number(selection.value),
        initial_charge: 0,
        management_charge: 0,
        factsheet: null,
        file: null
      });
    });

    setCurrentPortfolios(newPortfolios);
    setIsModelPortfolioSelectorOpen(false);
  };

  useEffect(() => {
    setIsLoading(true);
    getMpsFirms();
    api.getPortfolioCategories().then(({ data }: any) => {
      setCategories(data);
      setIsLoading(false);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setCurrentRange(data.portfolio_ranges[tab]);
  }, [tab, data.portfolio_ranges]);

  useEffect(() => {
    if (currentRange) {
      setCurrentPortfolios(currentRange.portfolios);
    }
  }, [currentRange]);

  return isLoading ? (
    <div className={classes.loaderWrapper}>
      <CircularProgress size="80px" />
    </div>
  ) : (
    <>
      {data.portfolio_ranges.length > 0 ? (
        <>
          <Tabs
            style={{
              margin: '1rem 0',
              borderBottom: '1px solid #d4d4d4',
              padding: '0 1rem'
            }}
            value={tab}
            onChange={handleTabChange}
            TabIndicatorProps={{
              style: { display: 'none' }
            }}
          >
            {data.portfolio_ranges.map((range: RangeType, index: number) => {
              const isActive =
                currentRange?.id === range.id ||
                currentRange?.name === range.name;
              return (
                <Tab
                  key={index}
                  {...tabProps(index)}
                  label={
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span></span>
                      <span>{range.name}</span>
                      <IconButton
                        size="small"
                        component="span"
                        style={{
                          color: isActive
                            ? 'rgba(255, 255, 255, 0.54)'
                            : 'rgba(0, 0, 0, 0.54)'
                        }}
                        onClick={() => {
                          setIsDeleteRangePromptOpen(true);
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                  }
                />
              );
            })}
            <div
              style={{
                padding: '0 1rem',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={addRange}
            >
              <AddCircleIcon color="secondary" />
            </div>
          </Tabs>
          <Formik
            enableReinitialize
            initialValues={{
              id: currentRange?.id,
              name: currentRange?.name,
              categories: categories.map((category: any) => ({
                ...category,
                is_checked: (currentRange?.categories || []).includes(
                  Number(category.value)
                )
              })),
              portfolios: currentPortfolios.map((portfolio: PortfolioType) => ({
                ...portfolio,
                factsheet: null,
                initial_charge: formatPercent(portfolio.initial_charge),
                management_charge: formatPercent(portfolio.management_charge),
                file: portfolio.factsheet
              }))
            }}
            onSubmit={values => {
              values.categories = values.categories
                .filter((category: any) => category.is_checked)
                .map((category: any) => Number(category.value));

              setIsSaving(true);

              const { portfolios, ...updatedValues } = values;

              const formData = new FormData();
              if (portfolios.length > 0) {
                buildFormData(formData, {
                  portfolios: portfolios.map((portfolio: PortfolioType) => {
                    if (portfolio.factsheet === null) {
                      delete portfolio.factsheet;
                    }

                    delete portfolio.file;

                    return {
                      ...portfolio,
                      initial_charge: unFormatPercent(portfolio.initial_charge),
                      management_charge: unFormatPercent(
                        portfolio.management_charge
                      )
                    };
                  })
                });
              } else {
                formData.append('portfolios', '[]');
              }

              let method = null;
              const type = values.id ? 'update' : 'create';
              if (type === 'create') {
                method = api.createPortfolioRange(data.id, updatedValues);
              } else {
                method = api.updatePortfolioRange(
                  data.id,
                  values.id as number,
                  updatedValues
                );
              }

              method
                .then((response: any) => {
                  api
                    .updatePortfolioRange(
                      data.id,
                      response.data.id as number,
                      formData
                    )
                    .then((response: any) => {
                      Notify.success(`Portfolio Range ${type}d successfully.`);
                      createOrUpdateRange(response.data);
                    });
                })
                .catch((e: any) => {
                  let message = `${values.name} Range could not be updated.`;
                  if (e.response.status === 400) {
                    try {
                      message = (
                        Object.values(e.response.data as any)[0] as any
                      )[0];
                    } catch (err) {
                      // do nothing
                    }
                  }
                  Notify.error(message);
                })
                .finally(() => {
                  setIsSaving(false);
                });
            }}
            validationSchema={validationSchema}
          >
            {({ handleSubmit, values }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <div>
                    <div className={classes.row}>
                      <div className={classes.label}>Portfolio Range</div>
                      <div className={classes.fieldWrapper}>
                        <Field name="name" component={Fields.v2.Text} filled />
                      </div>
                    </div>
                    <div className={`${classes.row} ${classes.alignBaseline}`}>
                      <div className={classes.label}>Category</div>
                      <Grid container style={{ width: '70%' }}>
                        {categories.map((category: any, index: number) => (
                          <Grid key={index} item xs={6} className={classes.row}>
                            <div style={{ marginRight: '0.5rem' }}>
                              <Field
                                component={Fields.v2.Checkbox}
                                name={`categories[${index}].is_checked`}
                              />
                            </div>
                            {category.name}
                          </Grid>
                        ))}
                      </Grid>
                    </div>
                    <div className={classes.row}>
                      <div className={classes.title}>Portfolios</div>
                    </div>
                    <Table className={classes.table}>
                      <TableComponents.TableHead
                        cells={[
                          'Name',
                          'Initial charge',
                          'Portfolio management charge',
                          'Factsheet',
                          ''
                        ]}
                      />
                      <TableComponents.TableBody>
                        {values.portfolios.map(
                          (portfolio: PortfolioType, index: number) => {
                            return (
                              <TableRow
                                key={portfolio.id}
                                style={{ cursor: 'pointer' }}
                              >
                                <Tables.TableCell className={classes.cell}>
                                  {portfolio.name}
                                </Tables.TableCell>
                                <Tables.TableCell className={classes.cell}>
                                  <Field
                                    name={`portfolios[${index}].initial_charge`}
                                    placeholder="0.00%"
                                    component={Fields.v2.Percent}
                                    style={{ width: '75px' }}
                                  />
                                </Tables.TableCell>
                                <Tables.TableCell className={classes.cell}>
                                  <Field
                                    name={`portfolios[${index}].management_charge`}
                                    placeholder="0.00%"
                                    component={Fields.v2.Percent}
                                    style={{ width: '75px' }}
                                  />
                                </Tables.TableCell>
                                <Tables.TableCell className={classes.cell}>
                                  <Field
                                    name={`portfolios[${index}].factsheet`}
                                    component={Fields.v2.FileUpload}
                                    url={portfolio.file}
                                    fileName={portfolio.file}
                                  />
                                </Tables.TableCell>
                                <Tables.TableCell
                                  width="36px"
                                  style={{ padding: 0 }}
                                >
                                  <div
                                    className={classes.iconHolder}
                                    onClick={e => {
                                      e.stopPropagation();
                                      setCurrentPortfolio({
                                        ...portfolio,
                                        index
                                      });
                                      setIsDeletePortfolioPromptOpen(true);
                                    }}
                                  >
                                    <Tooltip title="Delete" arrow>
                                      <DeleteOutlineIcon
                                        width="2.2rem"
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </Tooltip>
                                  </div>
                                </Tables.TableCell>
                              </TableRow>
                            );
                          }
                        )}
                      </TableComponents.TableBody>
                    </Table>
                  </div>
                  <div style={{ marginTop: '1rem' }}>
                    <Button
                      color="primary"
                      type="button"
                      disabled={isSaving}
                      onClick={() => {
                        setIsModelPortfolioSelectorOpen(true);
                      }}
                    >
                      Add new
                    </Button>
                  </div>
                  <div style={{ marginTop: '1rem' }}>
                    <Button
                      type="submit"
                      disabled={isSaving}
                      isLoading={isSaving}
                    >
                      Save
                    </Button>
                  </div>
                  <ModelPortfolioSelector
                    isOpen={isModelPortfolioSelectorOpen}
                    closeModelFunc={() =>
                      setIsModelPortfolioSelectorOpen(false)
                    }
                    addModelPortfolioSelection={(selections: any) =>
                      addPortfolio(selections, values)
                    }
                    addingModelPortfolioSelection={false}
                    investments={[]}
                    option="select"
                  />
                  {!!currentPortfolio && (
                    <>
                      <Prompt
                        isOpen={isDeletePortfolioPromptOpen}
                        title={`Are you sure you want to delete this portfolio?`}
                        confirmAction={() =>
                          deletePortfolio(
                            currentPortfolio.index as number,
                            values
                          )
                        }
                        isProcessing={isDeleting}
                        cancelAction={() =>
                          setIsDeletePortfolioPromptOpen(false)
                        }
                      />
                    </>
                  )}
                </form>
              );
            }}
          </Formik>
        </>
      ) : (
        <>
          <EmptyState
            title="No portfolio ranges"
            description="Get started by adding a new portfolio range."
          />
          <div style={{ display: 'flex' }}>
            <Button
              type="button"
              style={{
                margin: data.events.length === 0 ? '0 auto' : '1rem 0 0 0'
              }}
              onClick={addRange}
            >
              Add new
            </Button>
          </div>
        </>
      )}
      {!!currentRange && (
        <>
          <Prompt
            isOpen={isDeleteRangePromptOpen}
            title={`Are you sure you want to delete this range?`}
            confirmAction={() => deleteRange(currentRange?.id as number)}
            isProcessing={isDeleting}
            cancelAction={() => setIsDeleteRangePromptOpen(false)}
          />
        </>
      )}
    </>
  );
};

export default connect(null, {
  getMpsFirms: actions.cipActions.getMpsFirms
})(Portfolios);
